'use client';
import { useState } from 'react';
import type { ListStyleBlock, ProductListItem } from '../../lib/site';
import { Icon } from '../Icon';
import { CourseCard } from './CourseCard';
import { CourseRow } from './CourseRow';
import { ListControls } from './ListControls';
import { prefsFromComposedOrder, writePrefs, type ListPrefs } from './listPrefs';
import { productHref } from './href';

/**
 * 상품 묶음 — 첫 화면 상품 섹션(G)과 프로모션 `{lecture_listN}` 토큰이 쓴다.
 * 걸러 볼 것이 없는 자리라(관리자가 고른 묶음이다) `CourseList` 대신 이 한 겹을 쓴다.
 *
 * **카드는 강좌 목록과 같은 것(`CourseCard`)을 쓴다**(2026-08-22 사용자 제보로 교정).
 * 예전에는 여기에 카드 마크업이 따로 있었다 — 찜·배지·맛보기·차시/기간·단가 고르기·
 * 담기 버튼이 없고 배지 색이 박혀 있었으며 교재를 눌러도 `/lectures` 로 갔다.
 * 같은 자리에 두 벌의 카드가 있으면 **화면이 곧 갈라진다**(실제로 갈라져 있었다).
 *
 * **목록 전환·정렬은 자리마다 켠다**(2026-08-23 사용자 확정) — 관리자가 섹션·묶음마다
 * 정한다. 나머지(카드 형태·맛보기·미리보기·초기 정렬)는 설정 > 목록 스타일을 그대로 따른다.
 * 정렬은 **받은 항목을 그 자리에서** 한다 — 고른 묶음이라 서버에 다시 물을 것이 없다.
 *
 * 고른 뷰·정렬은 **자리마다 따로 기억한다**(2026-08-23 사용자 지적 — 목록 화면은 그러는데
 * 여기만 새로고침에 초기화됐다). `storageKey` 가 그 자리의 이름이다 — 첫 화면 둘째 섹션과
 * 프로모션 묶음이 서로의 기억을 덮어쓰면 안 된다.
 *
 * 기억은 **쿠키**라 서버가 첫 HTML 부터 옳게 그린다(`initial`) — 마운트 뒤에 고쳐 그리지
 * 않으므로 깜박임이 없다(2026-08-23 사용자 지적으로 localStorage 에서 옮겼다).
 *
 * **첫 정렬은 늘 '인기순'** 이다 — 이 자리의 인기순은 **관리자가 구성에서 담은 차례**다.
 * 목록 화면과 딱 이 한 가지가 다르고, 그 차이는 `prefsFromComposedOrder` 라는 이름으로
 * 사전(`listPrefs.ts`)에 두 정책을 나란히 적어 두었다 — 여기서 몰래 다르게 굴지 않는다.
 */
export function CourseGrid({
  items, style, priceDetail, storageKey, initial,
  viewToggle = false, sortControls = false, pagination = false,
}: {
  items: ProductListItem[];
  style: ListStyleBlock;
  priceDetail: boolean;
  /** 취향을 기억할 자리 이름 — `main:0` · `promo:20:lecture_list1` 처럼 자리마다 다르게 */
  storageKey: string;
  /** 서버가 쿠키에서 읽어 준 첫 취향 — 없으면 설정 기본값으로 그린다 */
  initial?: ListPrefs | null;
  /**
   * 나눠 보기 — 관리자 구성의 체크(현행 perpage 4 를 계승).
   * **정렬한 뒤에 자른다** — 예전에는 `PagedGrid` 가 먼저 자르고 `CourseGrid` 가 그 조각만
   * 정렬해서, 정렬이 **보고 있는 쪽 안에서만** 돌았다(2026-08-23 접으면서 교정).
   */
  pagination?: boolean;
  viewToggle?: boolean;
  sortControls?: boolean;
}) {
  const [prefs, setPrefs] = useState<ListPrefs>(() => initial ?? prefsFromComposedOrder(style));
  const [page, setPage] = useState(0);
  const change = (next: ListPrefs) => {
    setPrefs(next);
    writePrefs(storageKey, next);
    setPage(0); // 3쪽에서 정렬을 바꾸면 엉뚱한 구간을 본다(목록 화면과 같은 규칙)
  };

  if (!items.length) return null;

  // **정렬 → 자르기** 순서다. 뒤바뀌면 정렬이 보고 있는 쪽 안에서만 돈다
  const sorted = sortControls ? sortItems(items, prefs) : items;
  const pages = pagination ? Math.ceil(sorted.length / PER_PAGE) : 1;
  const shown = pagination ? sorted.slice(page * PER_PAGE, (page + 1) * PER_PAGE) : sorted;
  /*
   * 카드/리스트 — **전환 버튼이 없으면 관리자 설정의 기본 보기를 따른다**
   * (2026-08-27 사용자 원칙 "특별한 정의가 없다면 관리자 설정값이 디폴트").
   *
   * 전에는 `viewToggle && …` 이라 버튼이 없는 자리(연관상품·첫 화면 섹션·프로모션)는
   * 설정이 `리스트` 여도 **늘 카드**로 그렸다 — 관리자가 정한 기본을 화면이 무시한 것이다.
   * 버튼이 있으면 사용자가 고른 값(prefs), 없으면 설정값(style)이다.
   */
  const list = (viewToggle ? prefs.view : style.view) === 'list';

  return (
    <>
      <ListControls prefs={prefs} onPrefs={change} viewToggle={viewToggle} sortControls={sortControls} />
      {list ? (
        <div className={`course-list${style.listVariant === 'simple' ? ' simple-list' : ''}`}>
          {shown.map((p) => (
            <CourseRow key={p.id} p={p} variant={style.listVariant} priceDetail={priceDetail}
              freeVod={style.freeVod} hoverPreview={style.hoverPreview}
              href={productHref('/lectures', p)} />
          ))}
        </div>
      ) : (
        <div className={`course-grid${style.cardVariant === 'simple' ? ' simple-grid' : ''}`}>
          {shown.map((p) => (
            <CourseCard key={p.id} p={p} variant={style.cardVariant} priceDetail={priceDetail}
              freeVod={style.freeVod} hoverPreview={style.hoverPreview}
              href={productHref('/lectures', p)} />
          ))}
        </div>
      )}

      {/* 나눠 보기 — 가이드 페이저(page-btn). 한 쪽뿐이면 그리지 않는다 */}
      {pages > 1 && (
        <div className="pagination" style={{ justifyContent: 'center', marginTop: 14 }}>
          <button className="page-btn" disabled={page === 0} onClick={() => setPage(page - 1)} aria-label="prev">
            <Icon name="chevron-left" />
          </button>
          {Array.from({ length: pages }, (_, i) => (
            <button key={i} className={`page-btn${i === page ? ' active' : ''}`} onClick={() => setPage(i)}>{i + 1}</button>
          ))}
          <button className="page-btn" disabled={page === pages - 1} onClick={() => setPage(page + 1)} aria-label="next">
            <Icon name="chevron-right" />
          </button>
        </div>
      )}
    </>
  );
}

/** 현행 메인 상품 섹션 한 쪽 분량(perpage 4) */
const PER_PAGE = 4;

/**
 * 그 자리에서 정렬. **'인기순' 은 관리자가 구성에서 담은 차례 그대로**다(2026-08-23 확정) —
 * 목록 화면에서는 서버가 노출 순서(`sort` 칸)로 뽑아 주지만, 첫 화면 섹션·프로모션 묶음은
 * 관리자가 직접 배열한 순서가 있고 그것이 이 자리의 '인기' 다. 서버가 그 차례대로 준다
 * (`sub-index.service` 가 `pid` 순으로 맞춰 보낸다).
 * 이름은 로케일 비교(`localeCompare`)를 쓴다 — 한글·영문이 섞인 목록에서 코드값 순서는 뜻이 없다.
 */
function sortItems(items: ProductListItem[], prefs: ListPrefs): ProductListItem[] {
  if (prefs.sort === 'popular') return items;
  const sign = prefs.dir === 'asc' ? 1 : -1;
  const by = prefs.sort === 'name'
    ? (a: ProductListItem, b: ProductListItem) => a.name.localeCompare(b.name)
    : prefs.sort === 'price'
      ? (a: ProductListItem, b: ProductListItem) => a.price - b.price
      // 최신 — id 순이다. **목록 화면(서버)과 같은 기준**이다(`product-order.ts` 의
      // `q.order === LATEST → { id }`) — 등록일 칸으로 정렬하지 않는다
      : (a: ProductListItem, b: ProductListItem) => a.id - b.id;
  return [...items].sort((a, b) => by(a, b) * sign);
}
